import React, { useState, useRef, useEffect } from 'react';
import { Search, X, GraduationCap, FileText, ArrowRight } from 'lucide-react';

interface SearchResult {
  id: string;
  title: string;
  type: 'module' | 'resource';
  category?: string;
  description?: string;
}

// Searchable content
const SEARCHABLE_ITEMS: SearchResult[] = [
  // Modules
  { id: 'coffee-101', title: 'Coffee Business 101', type: 'module', description: 'Industry fundamentals' },
  { id: 'finding-niche', title: 'Finding Your Niche', type: 'module', description: 'Target market and personas' },
  { id: 'branding-basics', title: 'Branding Basics', type: 'module', description: 'Logo, packaging, storytelling' },
  { id: 'marketing-strategy', title: 'Marketing Strategy', type: 'module', description: 'Social media and acquisition' },
  { id: 'scaling-up', title: 'Scaling Up', type: 'module', description: 'Operations and growth' },
  { id: 'customer-retention', title: 'Customer Retention', type: 'module', description: 'Loyalty and lifetime value' },
  // Resources
  { id: 'business-plan', title: 'Business Plan Template', type: 'resource', category: 'planning' },
  { id: 'profit-calculator', title: 'Profit Calculator', type: 'resource', category: 'finance' },
  { id: 'launch-checklist', title: 'Launch Checklist', type: 'resource', category: 'planning' },
  { id: 'brand-guide', title: 'Brand Style Guide', type: 'resource', category: 'marketing' },
  { id: 'social-calendar', title: 'Content Calendar', type: 'resource', category: 'marketing' },
  { id: 'email-templates', title: 'Email Templates', type: 'resource', category: 'marketing' },
];

interface SearchBarProps {
  onResultClick: (result: SearchResult) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({ onResultClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (query.trim().length > 0) {
      const filtered = SEARCHABLE_ITEMS.filter(item =>
        item.title.toLowerCase().includes(query.toLowerCase()) ||
        item.description?.toLowerCase().includes(query.toLowerCase()) ||
        item.category?.toLowerCase().includes(query.toLowerCase())
      );
      setResults(filtered);
    } else {
      setResults([]);
    }
  }, [query]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleResultClick = (result: SearchResult) => {
    onResultClick(result);
    setIsOpen(false);
    setQuery('');
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-10 h-10 rounded-xl bg-neutral-100 flex items-center justify-center transition-all hover:bg-neutral-200 active:scale-95"
      >
        <Search className="w-5 h-5 text-neutral-600" />
      </button>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Search Input */}
      <div className="flex items-center bg-neutral-100 rounded-xl px-3 py-2 w-64">
        <Search className="w-4 h-4 text-neutral-400 mr-2 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search modules, tools..."
          className="flex-1 bg-transparent outline-none text-sm text-neutral-900 placeholder-neutral-400"
        />
        <button
          onClick={() => { setIsOpen(false); setQuery(''); }}
          className="ml-2 text-neutral-400 hover:text-neutral-600"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Results Dropdown */}
      {results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-lg border border-neutral-200/60 overflow-hidden z-50 max-h-80 overflow-y-auto">
          {results.map(result => (
            <button
              key={result.id}
              onClick={() => handleResultClick(result)}
              className="w-full p-3 flex items-center hover:bg-neutral-50 transition-colors text-left"
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                result.type === 'module' ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600'
              }`}>
                {result.type === 'module' ? (
                  <GraduationCap className="w-4 h-4" />
                ) : (
                  <FileText className="w-4 h-4" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-neutral-900 truncate">{result.title}</p>
                <p className="text-xs text-neutral-400 capitalize">
                  {result.type === 'module' ? 'Module' : result.category}
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-neutral-300 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* No Results */}
      {query.trim().length > 0 && results.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-lg border border-neutral-200/60 p-4 text-center z-50">
          <p className="text-sm text-neutral-400">No results found</p>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
